import { createContext, useCallback, useMemo, useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import config from "../Config";

const UserSessionContext = createContext({
    data: null,
    error: false,
    loading: true,
    refetch: () => null,
    logout: () => null
});

function UserSessionProvider({ children }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(0);

    const refetch = useCallback(() => {
        setRefresh((prev) => prev + 1);
    }, []);

    const logout = useCallback(async () => {
        const token = Cookies.get(config.authCookie);
        
        // Call logout API to deactivate token on server
        if (token) {
            try {
                await axios.post("/client/logout", {}, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
            } catch (err) {
                // Ignore errors - still logout locally even if API call fails
                console.error("Logout API error:", err);
            }
        }
        
        Cookies.remove(config.authCookie);
        setData(null);
        setError(true);
        setLoading(false);
    }, []);

    useEffect(() => {
        let isMounted = true;
        const token = Cookies.get(config.authCookie);

        if (!token) {
            if (isMounted) {
                setError(true);
                setLoading(false);
                setData(null);
            }
            return () => {
                isMounted = false;
            };
        }

        setLoading(true);

        axios
            .get("/client/get_current_user", {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            .then((response) => {
                if (!isMounted) return;
                setData(response.data);
                setError(false);
            })
            .catch(() => {
                if (!isMounted) return;
                setError(true);
            })
            .finally(() => {
                if (!isMounted) return;
                setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [refresh]);

    // Listen for login event to refetch user data
    useEffect(() => {
        const handleLogin = () => {
            refetch();
        };

        window.addEventListener('user:login', handleLogin);
        return () => window.removeEventListener('user:login', handleLogin);
    }, [refetch]);

    const value = useMemo(
        () => ({
            data,
            error,
            loading,
            refetch,
            logout
        }),
        [data, error, loading, refetch, logout]
    );

    return <UserSessionContext.Provider value={value}>{children}</UserSessionContext.Provider>;
}

export { UserSessionContext, UserSessionProvider };
